const WORDS_PER_MINUTE = 240;
const READ_TIME_BUFFER = 1500;
const MIN_AUTOPLAY_DELAY = 6000;
const MAX_AUTOPLAY_DELAY = 15000;
const SWIPE_DISTANCE = 48;

const wordSegmenter =
  typeof Intl.Segmenter === 'function'
    ? new Intl.Segmenter('en', { granularity: 'word' })
    : null;

export const countWords = (text) => {
  const normalizedText = text.trim();
  if (!normalizedText) return 0;

  if (!wordSegmenter) return normalizedText.split(/\s+/u).length;

  return Array.from(wordSegmenter.segment(normalizedText)).filter(
    (segment) => segment.isWordLike,
  ).length;
};

export const calculateAutoplayDelay = (text) => {
  const readTime = (countWords(text) / WORDS_PER_MINUTE) * 60_000;
  return Math.min(
    MAX_AUTOPLAY_DELAY,
    Math.max(MIN_AUTOPLAY_DELAY, Math.round(readTime + READ_TIME_BUFFER)),
  );
};

const initializeTestimonialSlider = (slider) => {
  const track = slider.querySelector('[data-testimonial-slider-track]');
  const viewport = slider.querySelector('.testimonial-slider__viewport');
  const slides = track
    ? Array.from(track.querySelectorAll(':scope > [data-testimonial]'))
    : [];
  const pagination = slider.querySelector(
    '[data-testimonial-slider-pagination]',
  );
  const dots = pagination
    ? Array.from(pagination.querySelectorAll('[data-testimonial-slider-dot]'))
    : [];
  const status = slider.querySelector('[data-testimonial-slider-status]');

  if (!track || slides.length < 2 || dots.length !== slides.length) return;

  const autoplayDelays = slides.map((slide) =>
    calculateAutoplayDelay(
      slide.querySelector('.testimonial__content')?.textContent ??
        slide.textContent ??
        '',
    ),
  );

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;
  let activeIndex = 0;
  let timer;
  let isHovered = false;
  let hasFocus = false;
  let isVisible = true;
  let pointerStart;
  let pointerFocusTarget;

  const canAutoplay = () =>
    !reducedMotion && !isHovered && !hasFocus && isVisible && !document.hidden;

  const schedule = () => {
    window.clearTimeout(timer);
    if (!canAutoplay()) return;
    timer = window.setTimeout(() => {
      showSlide((activeIndex + 1) % slides.length);
    }, autoplayDelays[activeIndex]);
  };

  const showSlide = (index, announce = false) => {
    activeIndex = (index + slides.length) % slides.length;
    slides.forEach((slide, slideIndex) => {
      slide.setAttribute(
        'aria-hidden',
        slideIndex === activeIndex ? 'false' : 'true',
      );
    });
    dots.forEach((dot, dotIndex) => {
      if (dotIndex === activeIndex) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    if (announce && status) {
      status.textContent = `Testimonial ${activeIndex + 1} of ${slides.length}`;
    }
    schedule();
  };

  slider.classList.add('is-enhanced');
  pagination.hidden = false;
  dots.forEach((dot, index) => {
    dot.addEventListener('click', (event) => {
      // Keyboard and assistive-technology activation must keep rotation paused.
      hasFocus = event.detail === 0;
      showSlide(index, true);
    });
  });
  slider.addEventListener(
    'keydown',
    () => {
      pointerFocusTarget = undefined;
      hasFocus = true;
      window.clearTimeout(timer);
    },
    true,
  );
  pagination.addEventListener('keydown', (event) => {
    let nextIndex;
    if (event.key === 'ArrowLeft') nextIndex = activeIndex - 1;
    if (event.key === 'ArrowRight') nextIndex = activeIndex + 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = slides.length - 1;
    if (nextIndex === undefined) return;
    event.preventDefault();
    showSlide(nextIndex, true);
    dots[activeIndex].focus();
  });
  (viewport ?? track).addEventListener('pointerenter', (event) => {
    if (event.pointerType === 'touch') return;
    isHovered = true;
    window.clearTimeout(timer);
  });
  (viewport ?? track).addEventListener('pointerleave', (event) => {
    if (event.pointerType === 'touch') return;
    isHovered = false;
    schedule();
  });
  slider.addEventListener('focusin', (event) => {
    hasFocus = !pointerFocusTarget?.contains(event.target);
    schedule();
  });
  slider.addEventListener('focusout', (event) => {
    if (slider.contains(event.relatedTarget)) return;
    hasFocus = false;
    pointerFocusTarget = undefined;
    schedule();
  });
  slider.addEventListener('pointerdown', (event) => {
    pointerFocusTarget = event.target;
    hasFocus = false;
    schedule();
    if (event.pointerType !== 'touch') return;
    pointerStart = { id: event.pointerId, x: event.clientX };
    window.clearTimeout(timer);
  });
  slider.addEventListener('pointerup', (event) => {
    pointerFocusTarget = undefined;
    if (!pointerStart || pointerStart.id !== event.pointerId) return;
    const distance = event.clientX - pointerStart.x;
    pointerStart = undefined;
    if (Math.abs(distance) >= SWIPE_DISTANCE) {
      showSlide(activeIndex + (distance < 0 ? 1 : -1), true);
    } else {
      schedule();
    }
  });
  slider.addEventListener('pointercancel', () => {
    pointerFocusTarget = undefined;
    pointerStart = undefined;
    schedule();
  });
  document.addEventListener('visibilitychange', schedule);

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(([entry]) => {
      isVisible = entry.isIntersecting;
      if (isVisible) schedule();
      else window.clearTimeout(timer);
    });
    observer.observe(slider);
  }

  showSlide(0);
};

export const initializeTestimonialSliders = () => {
  document
    .querySelectorAll('[data-testimonial-slider]')
    .forEach(initializeTestimonialSlider);
};
